import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import EmptyState from "../components/Help/EmptyState";
import { api } from "../lib/api";

import { useAuth } from "../lib/auth";
import { useEntity } from "../lib/entity";
import {
  FileText, Upload, Filter, Download, Trash2, X, Plus,
  HardDrive, FileCheck, Eye, FolderLock, FileSpreadsheet,
  FileImage, Building2
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Lease Agreement",
  "Insurance Policy",
  "Deed & Title",
  "Property Inspection",
  "Mortgage & Loan",
  "Tax Document",
  "Appraisal",
  "Receipt / Invoice",
  "Other",
];

export default function Documents() {
  const { can } = useAuth();
  const { activeEntityId } = useEntity();
  const [docs, setDocs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [storage, setStorage] = useState({ total_bytes: 0, readable_mb: 0 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProperty, setSelectedProperty] = useState("all");

  // Upload Modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "Lease Agreement",
    property_id: "",
    notes: "",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeEntityId && activeEntityId !== "all") params.entity_id = activeEntityId;
      if (selectedCategory !== "all") params.category = selectedCategory;
      if (selectedProperty !== "all") params.property_id = selectedProperty;

      const [dRes, pRes, sRes] = await Promise.all([
        api.get("/documents", { params }),
        api.get("/properties", { params: activeEntityId !== "all" ? { entity_id: activeEntityId } : {} }),
        api.get("/documents/storage-usage"),
      ]);
      setDocs(dRes.data || []);
      setProperties(pRes.data || []);
      setStorage(sRes.data || { total_bytes: 0, readable_mb: 0 });
    } catch (err) {
      toast.error("Failed to load documents");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeEntityId, selectedCategory, selectedProperty]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!formData.name) {
        // Strip extension for clean display name
        const cleanName = file.name.replace(/\.[^/.]+$/, "");
        setFormData((prev) => ({ ...prev, name: cleanName }));
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !formData.name || !formData.category) {
      toast.error("Please provide a file, document name, and category");
      return;
    }

    const data = new FormData();
    data.append("file", selectedFile);
    data.append("name", formData.name);
    data.append("category", formData.category);
    if (formData.property_id) data.append("property_id", formData.property_id);
    if (formData.notes) data.append("notes", formData.notes);

    setUploading(true);
    try {
      await api.post("/documents/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document vaulted securely");
      setUploadModalOpen(false);
      setSelectedFile(null);
      setFormData({ name: "", category: "Lease Agreement", property_id: "", notes: "" });
      loadData();
    } catch (err) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (did) => {
    if (!window.confirm("Are you sure you want to permanently delete this document?")) return;
    try {
      await api.delete(`/documents/${did}`);
      toast.success("Document removed");
      loadData();
    } catch {
      toast.error("Failed to delete document");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 KB";
    const k = 1024;
    if (bytes < k * k) return `${(bytes / k).toFixed(1)} KB`;
    return `${(bytes / (k * k)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType = "") => {
    if (mimeType.includes("pdf")) return <FileText className="text-red-400" size={20} />;
    if (mimeType.includes("image")) return <FileImage className="text-blue-400" size={20} />;
    if (mimeType.includes("sheet") || mimeType.includes("csv") || mimeType.includes("excel")) {
      return <FileSpreadsheet className="text-emerald-400" size={20} />;
    }
    return <FileCheck className="text-teal-400" size={20} />;
  };

  return (
    <Layout
      title="Document Vault"
      subtitle="Encrypted storage for leases, deeds, insurance policies & tax forms"
      actions={
        can.edit && (
          <button
            onClick={() => setUploadModalOpen(true)}
            data-testid="upload-document-btn"
            className="btn-primary text-xs sm:text-sm py-2 px-3 sm:px-4"
          >
            <Upload size={16} />
            <span>Upload Document</span>
          </button>
        )
      }
    >
      {/* Storage & Stat Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center gap-1.5">
            <FolderLock size={13} className="text-teal-400" /> Documents Vaulted
          </div>
          <div className="text-2xl font-black tabular-nums mt-1">{docs.length}</div>
          <div className="text-xs text-[var(--muted)] mt-0.5">Retained for IRS & audit compliance</div>
        </div>

        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center gap-1.5">
            <Building2 size={13} className="text-blue-400" /> Properties Covered
          </div>
          <div className="text-2xl font-black tabular-nums mt-1">{properties.length}</div>
          <div className="text-xs text-[var(--muted)] mt-0.5">Organized by property</div>
        </div>

        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center gap-1.5">
            <HardDrive size={13} className="text-purple-400" /> Synology NAS Storage
          </div>
          <div className="text-2xl font-black tabular-nums text-purple-400 mt-1">
            {storage.readable_mb} MB
          </div>
          <div className="text-xs text-[var(--muted)] mt-0.5">Persisted in /data/documents</div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs text-[var(--muted)] font-semibold uppercase tracking-wider">
          <Filter size={14} /> Filter:
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-1.5">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="lapen-input !w-auto text-xs py-1.5"
          >
            <option value="all">All Categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <HelpTip topic="document-vault" />
        </div>

        {/* Property Filter */}
        <select
          value={selectedProperty}
          onChange={(e) => setSelectedProperty(e.target.value)}
          className="lapen-input !w-auto text-xs py-1.5"
        >
          <option value="all">All Properties</option>
          <option value="none">Portfolio-wide (No property)</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-5 h-20 skeleton" />
          ))}
        </div>
      ) : docs.length === 0 ? (
        <EmptyState
          icon={FolderLock}
          title="No Documents in Vault"
          description="Securely store PDF leases, closing statements, deeds, and insurance policies on your NAS for permanent IRS audit defense."
          helpTopic="document-vault"
          helpText="About Audit Defense & Vault Storage"
          actionLabel="+ Upload Document"
          onAction={() => setUploadModalOpen(true)}
          testId="docs-empty-state"
        />
      ) : (

        <div className="grid grid-cols-1 gap-3">
          {docs.map((doc) => (
            <div
              key={doc.id}
              className="card card-hover p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              data-testid={`doc-row-${doc.id}`}
            >
              <div className="flex items-center gap-3.5 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[var(--surface-hover)] border border-[var(--border)] flex items-center justify-center shrink-0">
                  {getFileIcon(doc.mime_type)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm sm:text-base truncate">{doc.name}</span>
                    <span className="tag tag-muted">{doc.category}</span>
                    {doc.property_name && (
                      <span className="text-xs text-teal-400 font-semibold truncate">
                        · {doc.property_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[var(--muted)] mt-0.5">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>·</span>
                    <span>Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}</span>
                    {doc.notes && (
                      <>
                        <span>·</span>
                        <span className="truncate max-w-[200px]">{doc.notes}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                <a
                  href={`/api/documents/${doc.id}/file`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary text-xs py-1.5 px-3"
                  title="View / Download file"
                >
                  <Eye size={14} /> View / Download
                </a>

                {can.edit && (
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="p-2 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Delete document"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Document Modal */}
      {uploadModalOpen && (
        <div className="sheet-backdrop" onClick={() => setUploadModalOpen(false)}>
          <div className="sheet-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-4 mb-4">
              <h2 className="text-lg font-bold">Vault New Document</h2>
              <button onClick={() => setUploadModalOpen(false)} className="p-1 rounded-lg hover:bg-[var(--surface-hover)]">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="label">Select File *</label>
                <div className="border-2 border-dashed border-[var(--border)] rounded-2xl bg-[var(--surface-hover)]/40 p-6 text-center">
                  <FileText size={32} className="text-teal-400 mx-auto mb-2" />
                  <p className="text-xs text-[var(--muted)] mb-3">PDF, Word, Excel, or Images up to 30MB</p>
                  <label className="btn-secondary text-xs cursor-pointer py-1.5 px-3">
                    <span>{selectedFile ? selectedFile.name : "Choose File"}</span>
                    <input
                      type="file"
                      required
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              <div>
                <label className="label">Document Display Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Master Lease 2026-2027"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="lapen-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="lapen-input"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label">Associated Property</label>
                  <select
                    value={formData.property_id}
                    onChange={(e) => setFormData({ ...formData, property_id: e.target.value })}
                    className="lapen-input"
                  >
                    <option value="">Portfolio-wide / Corporate</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Notes / Description</label>
                <input
                  type="text"
                  placeholder="e.g. Countersigned by tenant on March 15"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="lapen-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
                <button
                  type="button"
                  onClick={() => setUploadModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="btn-primary"
                >
                  <Upload size={16} />
                  <span>{uploading ? "Vaulting..." : "Vault Document"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
